import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import { useToast } from '../../components/common/Toast';
import { ArrowLeftIcon, XIcon, UserIcon, PhoneIcon, MapPinIcon } from '../../components/icons/Icons';
import barterService from '../../services/barter.service';
import { formatCurrency } from '../../utils/helpers';

const indigo = {
  50:  '#eef2ff',
  100: '#e0e7ff',
  400: '#818cf8',
  500: '#6366f1',
  600: '#4f46e5',
  700: '#4338ca',
};

const BarterExchangeRequests = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, [id]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await barterService.getExchangeRequests(id);
      if (res.success) {
        setRequests(res.data.requests);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load exchange requests');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this exchange request?')) return;
    
    try {
      setProcessing(requestId);
      await barterService.deleteExchangeRequest(requestId);
      toast.success('Exchange request deleted');
      fetchRequests();
    } catch (err) {
      toast.error('Failed to delete request');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    return (
      <span style={{
        background: '#e0e7ff',
        color: '#4338ca',
        fontSize: '11px',
        fontWeight: '700',
        borderRadius: '6px',
        padding: '4px 10px',
        textTransform: 'uppercase',
      }}>
        Pending
      </span>
    );
  };

  return (
    <MainLayout>
      <div style={{ padding: '32px 24px', maxWidth: '1200px', margin: '0 auto' }}>
        
        <button
          onClick={() => navigate('/seller/barter')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            border: `1px solid ${indigo[200]}`,
            borderRadius: '8px',
            background: '#fff',
            color: indigo[600],
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            marginBottom: '24px',
          }}
        >
          <ArrowLeftIcon size={16} /> Back to Listings
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '32px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '13px',
            background: `linear-gradient(135deg, ${indigo[500]}, ${indigo[700]})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <UserIcon size={24} color="#fff" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1e1b4b' }}>Exchange Requests</h1>
            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>{requests.length} request{requests.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#9ca3af' }}>Loading…</div>
        ) : requests.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '80px 32px',
            background: '#fff', borderRadius: '16px',
            border: `1.5px dashed ${indigo[100]}`,
          }}>
            <UserIcon size={48} color={indigo[400]} />
            <h3 style={{ margin: '16px 0 0 0', color: '#1e1b4b', fontWeight: '700' }}>No exchange requests yet</h3>
            <p style={{ color: '#6b7280', marginTop: '6px', fontSize: '14px' }}>
              Customers will see your listing and can submit exchange requests.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {requests.map((req) => (
              <div
                key={req.id}
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,.06), 0 4px 20px rgba(99,102,241,.07)',
                  border: '1px solid #e8eaf6',
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: '#1e1b4b' }}>
                        {req.product_name}
                      </h3>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        Submitted {new Date(req.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    {getStatusBadge(req.status)}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Customer</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <UserIcon size={16} color={indigo[600]} />
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e1b4b' }}>
                          {req.user.first_name} {req.user.last_name}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>{req.user.email}</div>
                    </div>

                    <div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Contact</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <PhoneIcon size={16} color={indigo[600]} />
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e1b4b' }}>{req.contact_number}</span>
                      </div>
                      {req.user.phone && (
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>Alt: {req.user.phone}</div>
                      )}
                    </div>

                    <div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Product Details</div>
                      <div style={{ fontSize: '14px', color: '#1e1b4b', marginBottom: '4px' }}>
                        <strong>Condition:</strong> <span style={{ textTransform: 'capitalize' }}>{req.condition}</span>
                      </div>
                      <div style={{ fontSize: '14px', color: '#1e1b4b' }}>
                        <strong>Value:</strong> {formatCurrency(req.expected_value)}
                      </div>
                    </div>
                  </div>

                  {req.description && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Description</div>
                      <p style={{ margin: 0, fontSize: '14px', color: '#4b5563', lineHeight: '1.6' }}>{req.description}</p>
                    </div>
                  )}

                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <MapPinIcon size={14} /> Address
                    </div>
                    <p style={{ margin: 0, fontSize: '14px', color: '#4b5563', lineHeight: '1.6' }}>{req.address}</p>
                  </div>

                  {req.images && req.images.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', marginBottom: '10px' }}>Product Images</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                        {req.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Product ${idx + 1}`}
                            style={{
                              width: '100%',
                              height: '120px',
                              objectFit: 'cover',
                              borderRadius: '10px',
                              border: '1px solid #e5e7eb',
                              cursor: 'pointer',
                            }}
                            onClick={() => window.open(img, '_blank')}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {req.seller_notes && (
                    <div style={{ marginBottom: '20px', padding: '12px', background: indigo[50], borderRadius: '8px', border: `1px solid ${indigo[100]}` }}>
                      <div style={{ fontSize: '11px', color: indigo[700], fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Your Notes</div>
                      <p style={{ margin: 0, fontSize: '14px', color: indigo[900] }}>{req.seller_notes}</p>
                    </div>
                  )}

                  <div style={{ paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                    <button
                      onClick={() => handleDeleteRequest(req.id)}
                      disabled={processing === req.id}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1.5px solid #fee2e2',
                        borderRadius: '10px',
                        background: '#fff5f5',
                        color: '#dc2626',
                        fontWeight: '700',
                        fontSize: '14px',
                        cursor: processing === req.id ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: processing === req.id ? 0.6 : 1,
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => !processing && (e.currentTarget.style.background = '#fee2e2')}
                      onMouseLeave={(e) => !processing && (e.currentTarget.style.background = '#fff5f5')}
                    >
                      <XIcon size={16} /> Delete Request
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default BarterExchangeRequests;
